import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import {
  borderPrimary,
  mediumText,
  extraSmallTextSize,
  baseTextSize,
  secondaryText,
} from '../../styling/stylingHelper';
import { MultiSelectFilter } from './MultiSelectFilter';
import { TabbedSections } from './TabbedSections';
import { DropdownSelector } from './DropdownSelector';
import { Filter } from 'lucide-react';
import { getCurrentStep, useOnboardingStore } from '../onboarding/onboardingStore';
import { ONBOARDING_Z_INDEX } from '../onboarding/constants';

/**
 * See ColumnFilterChoiceConfig in TableComponent — a single-select section in this dropdown.
 * `options[0]` MUST be the neutral / unset choice: it is what the active-state indicator
 * (green funnel + count badge) compares against.
 */
interface ChoiceConfig {
  presentation?: 'tabs' | 'dropdown';
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

/** True when a choice section is set to anything other than its neutral first option. */
const isChoiceActive = (config?: ChoiceConfig) => !!config && config.value !== config.options[0]?.value;

/**
 * One single-select block (label + segmented control). Kept local because both the extra
 * `choice` section and the `sort` section render identically.
 */
const ChoiceSection: React.FC<{ config: ChoiceConfig }> = ({ config }) => (
  <div>
    <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>{config.label}</label>
    {config.presentation === 'dropdown' ? <DropdownSelector options={config.options}
      defaultValue={config.value} onChange={config.onChange} alwaysFullWidth /> : <TabbedSections
      options={config.options}
      value={config.value}
      onChange={config.onChange}
      isTimeOptionSelection={false}
      // Never collapse into a dropdown-inside-a-dropdown: these lists are short and fixed.
      maxTabsBeforeDropdown={config.options.length}
      fullWidth
    />}
  </div>
);

interface ColumnFilterDropdownProps {
  onboardingId?: string;
  // Multi-select filter
  selectedValues?: string[];
  onValuesChange?: (values: string[]) => void;
  availableOptions?: string[] | { id: string; name: string }[];
  valueType?: 'default' | 'category' | 'subcategory' | 'blueQuantity';
  filterLabel?: string;
  maxHeight?: string;
  // Subcategory filter options (shown below main filter)
  subCategorySelectedValues?: string[];
  subCategoryOnValuesChange?: (values: string[]) => void;
  subCategoryAvailableOptions?: string[];
  subCategoryFilterLabel?: string;
  // Dropdown alignment: 'center' (default) or 'left'
  align?: 'center' | 'left';
  // Optional single-select section under the multi-select list.
  choice?: ChoiceConfig;
  // Optional sort control for this column (for columns without header chevrons).
  sort?: ChoiceConfig;
}

export const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({
  onboardingId,
  selectedValues = [],
  onValuesChange,
  availableOptions = [],
  valueType = 'default',
  filterLabel,
  maxHeight,
  subCategorySelectedValues = [],
  subCategoryOnValuesChange,
  subCategoryAvailableOptions = [],
  subCategoryFilterLabel = 'Subcategories',
  align = 'center',
  choice,
  sort,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tourTarget = useOnboardingStore(() => {
    const step = getCurrentStep();
    return step?.allowTargetInteraction ? step.target : undefined;
  });
  const controlIds = onboardingId?.split(' ').map(id => `${id}-control`).join(' ');
  const inTour = Boolean(controlIds && tourTarget && controlIds.split(' ').includes(tourTarget));
  // A portalled menu must join the spotlight's pointer and keyboard scope.
  // Close it when this step ends, including Back, Escape and navigation.
  useEffect(() => {
    if (!inTour) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      setIsOpen(false);
    };
  }, [inTour]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Hidden until measured, so the panel never flashes at a stale position on open.
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  // Close dropdown when clicking outside. The panel is portalled out of this subtree, so it
  // needs its own containment check.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  /**
   * The panel renders in a portal on <body> rather than inside the header cell: the table sits
   * in an `overflow-x-auto` wrapper, and a container with one axis set to auto computes the
   * other to auto too — so an in-flow panel gets cut off vertically as soon as the table is
   * short (a filter leaving one or two rows would hide its own options). Fixed positioning is
   * recomputed from the trigger on every scroll/resize while open.
   */
  const repositionPanel = useCallback(() => {
    const trigger = dropdownRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const width = panelRef.current?.offsetWidth ?? 400;
    const margin = 8;
    const unclamped = align === 'left' ? trigger.left : trigger.left + trigger.width / 2 - width / 2;
    const left = Math.min(Math.max(margin, unclamped), Math.max(margin, window.innerWidth - width - margin));
    setPanelStyle({ position: 'fixed', top: trigger.bottom + 4, left, visibility: 'visible' });
  }, [align]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelStyle({ visibility: 'hidden' });
      return;
    }
    repositionPanel();
    // Capture phase so the table's own horizontal scroller is heard, not just the window.
    window.addEventListener('scroll', repositionPanel, true);
    window.addEventListener('resize', repositionPanel);
    return () => {
      window.removeEventListener('scroll', repositionPanel, true);
      window.removeEventListener('resize', repositionPanel);
    };
  }, [isOpen, repositionPanel]);

  // The multi-select list is optional now: a column may open this dropdown purely for its
  // choice / sort sections. Render nothing only when there is no section at all to show.
  const showMultiSelect = availableOptions.length > 0 && !!onValuesChange;
  if (!showMultiSelect && !choice && !sort) {
    return null;
  }

  const activeChoiceCount = (isChoiceActive(choice) ? 1 : 0) + (isChoiceActive(sort) ? 1 : 0);
  const totalFilterCount = selectedValues.length + (subCategorySelectedValues?.length || 0) + activeChoiceCount;
  const hasActiveFilters = totalFilterCount > 0;

  return (
    <div className="relative inline-block ml-1.5" ref={dropdownRef}>
      {/* Filter Icon Button */}
      <button
        data-onboarding={controlIds}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={cn(
          "relative flex items-center justify-center",
          "transition-colors",
          hasActiveFilters
            ? "text-darkGreenColor dark:text-darkGreenColor-dark"
            : "text-[#344054] dark:text-textSecondary-dark hover:text-[#475467] dark:hover:text-textPrimary-dark"
        )}
        aria-label="Filter column"
      >
        <Filter className="w-3 h-3" />

        {/* Badge showing count of selected filters */}
        {hasActiveFilters && (
          <span
            className={cn(
              "absolute -top-1 -right-1",
              "flex items-center justify-center",
              "min-w-[14px] h-[14px] px-0.5",
              "bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white",
              "rounded-full",
              extraSmallTextSize,
              mediumText
            )}
          >
            {totalFilterCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && createPortal(
        <div
          ref={panelRef}
          data-onboarding-filter-panel={inTour ? tourTarget : undefined}
          style={{ ...panelStyle, ...(inTour ? { zIndex: ONBOARDING_Z_INDEX + 1 } : {}) }}
          className={cn(
            "z-50",
            // Mobile: cap at viewport width so the 400px popover can't overflow a phone;
            // re-assert the exact desktop 400px at sm: so ≥sm is byte-identical.
            "w-[95vw] sm:w-[400px]",
            "bg-white dark:bg-background-dark shadow-xl rounded-xl border",
            borderPrimary,
            "p-3",
            // Header cells are centre-aligned and the panel used to inherit that; on <body>
            // it no longer would, so keep it explicit and leave every table looking the same.
            "text-center"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {showMultiSelect && (
            <MultiSelectFilter
              label={filterLabel || "Filter"}
              selectedValues={selectedValues}
              onValuesChange={onValuesChange!}
              availableOptions={availableOptions}
              valueType={valueType}
              maxHeight={maxHeight || "max-h-[170px]"}
            />
          )}
          {/* Subcategory Filter - shown below main filter if available */}
          {subCategoryAvailableOptions && subCategoryAvailableOptions.length > 0 && subCategoryOnValuesChange && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-borderGrey-dark">
              <MultiSelectFilter
                label={subCategoryFilterLabel}
                selectedValues={subCategorySelectedValues}
                onValuesChange={subCategoryOnValuesChange}
                availableOptions={subCategoryAvailableOptions}
                valueType="subcategory"
                maxHeight="max-h-[170px]"
              />
            </div>
          )}
          {/* Optional single-select sections; the divider only appears when something sits above. */}
          {choice && (
            <div className={cn(showMultiSelect && "mt-4 pt-4 border-t border-gray-200 dark:border-borderGrey-dark")}>
              <ChoiceSection config={choice} />
            </div>
          )}
          {sort && (
            <div className={cn((showMultiSelect || choice) && "mt-4 pt-4 border-t border-gray-200 dark:border-borderGrey-dark")}>
              <ChoiceSection config={sort} />
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};
